import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import UiIcon from '../../components/common/UiIcon';

export const PaymentFailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = searchParams.get('code') || 'UNKNOWN_ERROR';
  const message = searchParams.get('message') || '알 수 없는 이유로 결제가 중단되었습니다.';
  const orderId = searchParams.get('orderId');

  return (
    <div className="min-h-screen bg-[#110d0c] px-4 py-8 text-white md:px-6 lg:px-8">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.08),transparent_28%),radial-gradient(circle_at_80%_20%,rgba(189,59,15,0.14),transparent_22%),linear-gradient(180deg,#110d0c_0%,#191210_45%,#110d0c_100%)]" />
      <div className="pointer-events-none fixed inset-0 bg-grain opacity-20 mix-blend-overlay" />

      <div className="relative z-10 mx-auto grid max-w-4xl gap-6 lg:grid-cols-[0.94fr_1.06fr]">
        <section className="border border-[#fbbf24]/15 bg-[#16110e]/82 p-6 shadow-[0_30px_80px_-35px_rgba(0,0,0,0.95)] backdrop-blur-sm md:p-7">
          <p className="text-[11px] uppercase tracking-[0.26em] text-[#fbbf24]/58">결제 실패</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-[0.02em] text-[#f8e7cf] md:text-4xl">
            결제가 끝까지 이어지지 않았습니다
          </h1>
          <p className="mt-5 text-sm leading-7 text-[#ddc6a8]/78">
            카드 리딩 세션으로 넘어가기 전에 결제 단계에서 문제가 발생했습니다. 대부분은 인증 실패, 결제 수단 이슈, 네트워크 문제에서 다시 시도하면 복구됩니다.
          </p>

          <div className="mt-6 border border-[#fbbf24]/15 bg-black/20 p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#fbbf24]/52">오류 스냅샷</p>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-start justify-between gap-4">
                <dt className="text-[#bda78a]">오류 코드</dt>
                <dd className="font-medium text-[#f8ead6]">{code}</dd>
              </div>
              {orderId && (
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-[#bda78a]">주문번호</dt>
                  <dd className="font-medium text-[#f8ead6]">{orderId}</dd>
                </div>
              )}
            </dl>
          </div>
        </section>

        <section className="border border-[#fbbf24]/18 bg-[#f8f5f0] p-6 text-black shadow-[0_30px_80px_-35px_rgba(0,0,0,0.95)] md:p-7">
          <div className="flex items-center gap-3 text-[#b34c43]">
            <UiIcon name="warning" className="h-9 w-9" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#8a6a4b]">복구 안내</p>
              <h2 className="mt-1 text-2xl font-semibold text-[#18110e]">다시 이어갈 수 있습니다</h2>
            </div>
          </div>

          <p className="mt-5 text-sm leading-7 text-[#5b463b]">{message}</p>

          <div className="mt-6 space-y-3 rounded-2xl border border-[#eadfce] bg-white p-5 shadow-sm">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#8a6a4b]">다음에 해볼 것</p>
            <ul className="space-y-3 text-sm leading-6 text-[#5b463b]">
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c5a065]" />
                <span>같은 질문과 스프레드로 다시 결제를 시도해 보세요.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c5a065]" />
                <span>카드사 인증이나 결제 수단 문제가 있다면 다른 결제 수단으로 바꾸는 편이 빠를 수 있습니다.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c5a065]" />
                <span>질문부터 다시 정리하고 싶다면 스프레드 선택으로 돌아가 새 세션을 시작할 수 있습니다.</span>
              </li>
            </ul>
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex w-full items-center justify-center rounded-xl bg-[#18110e] px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-[#241811]"
            >
              다시 시도하기
            </button>
            <button
              type="button"
              onClick={() => navigate('/spreads')}
              className="inline-flex w-full items-center justify-center rounded-xl border border-[#c9b8a7] bg-white px-5 py-4 text-sm font-semibold text-[#3c2e26] transition-colors hover:bg-[#faf4ee]"
            >
              스프레드 다시 고르기
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex w-full items-center justify-center rounded-xl border border-[#eadfce] bg-[#fffaf4] px-5 py-4 text-sm font-semibold text-[#5b463b] transition-colors hover:bg-[#f8efe3]"
            >
              홈으로 돌아가기
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};
